import { useCallback, useEffect, useMemo, useState } from 'react';
import CampaignFilter from './CampaignFilter';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useAccountSettings } from '../../hooks/useAccountSettings';
import { getUserData } from '../../utils/userUtils';
import { isSuperAdmin } from '../../utils/permissions';
import { downloadJson } from '../../utils/downloadJson';
import { downloadPerformanceExcel } from '../../utils/performanceExcelExport';
import { buildPerformanceTabExport, performanceExportFilename } from '../../utils/performanceRawExport';
import {
  usePerformanceRuns, useApplySuggestion, useDismissals, useRefreshAnalyses,
} from './usePerformance';
import { partitionDismissed, partitionApplied } from '../../utils/performanceCards';
import { cardEntityKeys } from '../../utils/deliveryBlockers';
import { diagnosisCards } from '../../utils/diagnosisFindings';
import { mainCards } from '../../utils/findingTier';
import CardTab from './CardTab';
import PortfolioTab from './Portfolio/PortfolioTab';
import PerformanceSidekick from './PerformanceSidekick';
import {
  describeRefresh, describeRefreshProgress, newestRunCompletedAt, runFreshness, describeDataFreshness,
} from '../../utils/performanceRefresh';
import './Performance.css';

/**
 * The Performance section — Summary, Diagnostics, Budget & pacing and
 * Optimization, over the runs that already completed for a campaign.
 *
 * GATED ON `enable-performance-tab`, per account, in BOTH halves. This component
 * hides the section; routes/v2/performance.js refuses it. The frontend gate is
 * the UX affordance and the backend one is the enforcement point — the section
 * exposes an account's live delivery data and its optimizer's suggestions, so
 * "this account has not enabled Performance" has to be a refusal the server
 * makes, not a control the client chooses not to draw. Same reasoning as
 * utils/optimizeCampaignFlag.js.
 *
 * The five content tabs are read-only. The separate Sidekick rail deliberately
 * reuses the existing Optimization agent and its explicit Run control; merely
 * opening this page or changing a content tab never starts an optimizer run.
 */

/**
 * The line shown when a reason is picked that does NOT mean the finding was
 * wrong. Mirrors NOT_A_REJECTION in apps/app-backend/utils/performanceDismissals.js
 * — the server derives the outcome; this only tells the person what will happen,
 * at the moment they choose. Without it, dismissing reads as "stop telling me".
 */
const NOT_A_REJECTION_NOTES = {
  'Already actioned outside Stellair': 'This says the finding was correct — it will be recorded as resolved elsewhere, not as a rejection, so the check keeps running.',
  'Wrong timing': 'Recorded as a deferral, not a rejection. The check re-raises after the snooze window.',
  'Being fixed outside Stellair': 'Recorded as resolved elsewhere. The check keeps running so it can confirm the fix landed.',
  'Budget is fixed by the client': 'Recorded as a constraint, not a rejection — the finding stays valid and returns when the budget can move.',
  'Client has been told': 'Recorded as acknowledged. The alert stays open until the underlying status clears.',
  'No budget available': 'Recorded as a constraint, not a rejection — the finding stays valid and returns when budget frees up.',
};

/**
 * Four tabs. Three are backed by one agent run each:
 *   Diagnostics       the `diagnostic` focused analysis
 *   Budget & pacing   the `pacing` focused analysis
 *   Optimization      the `performance` focused analysis
 *
 * No tab for the combined optimize-campaign run. The judge runs on each focused
 * analysis, so its verdicts and suppressed set travel with the run they belong to
 * — they render under the tab that produced them rather than on a page of their
 * own.
 *
 * Summary is the odd one out and comes first: it is not an agent's findings, it
 * is the account's delivery, with those findings folded in as a health read. It
 * is the default because it is the question someone has before they have a
 * reason to open any of the other three — "how is this account doing" precedes
 * "what did the diagnostic say". It carries no badge for the same reason: a
 * badge counts a queue of things to read, and a dashboard is not a queue.
 *
 * The keys are the URL's `?tab=` values and are deliberately NOT renamed with
 * the labels — `recommendations` is also the card `route` the server tags each
 * suggestion with, so renaming it here would orphan every existing link and
 * every stored card.
 */
const TABS = [
  { key: 'account', label: 'Summary', icon: 'dashboard' },
  { key: 'diagnostics', label: 'Diagnostics', icon: 'fact_check' },
  { key: 'pacing', label: 'Budget & pacing', icon: 'speed' },
  { key: 'recommendations', label: 'Optimization', icon: 'lightbulb' },
];

/* Methodology copy, per tab. Every figure quoted here is a constant in the
   pacing math or the evidence contract, not a rounded-off recollection:
   BUDGET_CAP_THRESHOLD, RECENT_WINDOW_DAYS and ON_PACE_TOLERANCE live in
   campaign_optimizer/gads_pacing_math.py. */
const METHODOLOGY = {
  pacing: [
    ['In code', 'Every figure a pacing card turns on is computed in code and handed to the model '
      + 'as authoritative. The model decides what to do about a gap; it never works out whether '
      + 'there is one.'],
    ['Capped day', 'A complete day on which spend reached 95% of the daily budget. Not 100%: '
      + 'Google overdelivers slightly on one day and underdelivers the next inside the same '
      + 'monthly charging limit, so an exact test would miss days a trader would call capped.'],
    ['Run rate', 'Mean daily spend over the last 7 complete days. Today is excluded because it is '
      + 'still accruing, and including it drags the projection down by whatever fraction of the '
      + 'day has not happened yet.'],
    ['Goal', 'Taken from the media plan first, the campaign’s own total budget second, and '
      + 'average daily spend / daily budget for daily-only targets. Which one was used is '
      + 'recorded in goal_basis; daily-only targets never imply a booked flight total.'],
    ['On pace', 'Within 10% of the straight line. Wide enough that ordinary auction noise does not '
      + 'read as a problem, narrow enough that a real miss is named while there is still flight '
      + 'left to fix it.'],
  ],
  diagnostics: [
    ['Coverage', 'The diagnostic asks whether each entity can physically serve, and reads only '
      + 'what the platform itself reports: serving and review status, the reasons behind it, and '
      + 'the signals a bid strategy needs — ad groups, ads and keywords on Search, asset groups '
      + 'and their required assets on Performance Max. It does not check tracking against GA4 or '
      + 'the client’s site, and there is no crawler, so a broken tag on the site is invisible to '
      + 'it.'],
    ['Contested targets', 'Several targets also get advice from the Performance agent under a '
      + 'different action. Where that happens the card says so and names the tab, because applying '
      + 'either alone is defensible while applying both in sequence may not be.'],
  ],
  recommendations: [
    ['Expected lift', 'A percentage with a range, normalized to a 30-day month from the reporting '
      + 'window’s actual day count — never a raw window sum. Where no counterfactual '
      + 'exists the analysis says so and keeps the range wide.'],
    ['Confidence', 'Capped by the channel before anything else — 65 on Performance Max, 85 on '
      + 'Search — and lower again for a suggestion that infers rather than measures (45 and 50). '
      + 'Thin data overrides both: under 15 conversions, or under 100 clicks on Search, the '
      + 'ceiling is 40. Nothing may exceed 99. These are clamped in code after the analysis '
      + 'returns, not merely asked for — which is why a PMax run rarely shows anything above the '
      + 'low 60s.'],
    ['Cost of inaction', 'The counterfactual over 30 days, not a restatement of the gain. Where the '
      + 'honest answer is "nothing gets worse, the campaign just stays this size", it says exactly '
      + 'that.'],
  ],
};

export default function PerformancePage() {
  const { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse();
  // Account settings and pending actions belong to one account. Remount them
  // together so an account switch cannot reuse the previous account's opt-in.
  return <PerformanceAccountPage key={accountId} accountId={accountId} />;
}

export function PerformanceAccountPage({ accountId, embedded = false, savedReport, refreshKey = 0, initialTab = 'account', buildEditRequest, onApplied, actionsOnly = false, summaryContent, onOverviewCampaign, overviewScope, campaignScopeId = null, recommendationSections = null }) {
  const params = useParams();
  const campaignId = campaignScopeId || (embedded ? null : params.campaignId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [embeddedTab, setEmbeddedTab] = useState(() => TABS.some(tab => tab.key === initialTab) ? initialTab : 'account');
  const [selectedCampaignIds, setCampaignIds] = useState([]);
  const campaignIds = useMemo(() => campaignScopeId ? [campaignScopeId] : savedReport ? savedReport.rows.map(row => row.campaignId) : selectedCampaignIds,
    [campaignScopeId, savedReport, selectedCampaignIds]);
  const [user] = useState(() => getUserData());
  const [sidekickOpen, setSidekickOpen] = useState(false);
  // Gates the raw-JSON export. isSuperAdmin(user) alone goes FALSE during
  // impersonation — the impersonate route returns the TARGET user's
  // permissions — but an impersonating session is always driven by a super
  // admin (the route sits behind the isSuperAdmin middleware), so the export
  // stays available while looking at an account as one of its users. Same
  // reasoning as CampaignDetailPage's actingAsSuperAdmin.
  const actingAsSuperAdmin = isSuperAdmin(user) || !!user?.isImpersonating;
  // Summary's own fetches (grid, series, deltas, windowed), handed up by
  // PortfolioTab so the export button in the strip can include them.
  const [portfolioRaw, setPortfolioRaw] = useState(null);
  const [exportError, setExportError] = useState(null);
  const {
    settings: accountSettings,
    getSetting,
    isLoading: settingsLoading,
  } = useAccountSettings(accountId, user);

  const tab = embedded ? embeddedTab : searchParams.get('tab') || 'account';
  const setTab = (key) => embedded ? setEmbeddedTab(key) : setSearchParams((p) => {
    const next = new URLSearchParams(p);
    next.set('tab', key);
    return next;
  }, { replace: true });

  const enabled = getSetting('enable-performance-tab') === 'true'
    || (embedded && getSetting('performance-v2.0') === 'true');
  const performanceV2 = getSetting('performance-v2.0') === 'true';

  const userId = user?.userId || user?.id || null;
  // Account-wide: the daily cron analyses every campaign live in its DSP, so the
  // tabs answer "what did the agents find across the account". A campaignId in
  // the URL narrows to one campaign as a drill-down.
  const runs = usePerformanceRuns(enabled ? accountId : null, userId, campaignId, embedded ? campaignIds : [], Boolean(savedReport || campaignScopeId), savedReport?.rows);
  const campaignCatalog = [...new Map([
    ...runs.campaigns,
    ...(portfolioRaw?.grid?.campaigns || []),
  ].map(campaign => [campaign.id, campaign])).values()];

  // Applies dispatch through the existing /campaigns/:id/edit path. The
  // destination comes from whichever run resolved it — both endpoints return the
  // campaign, and the edit builder needs it to shape the request per platform.
  const destination = runs.campaign?.destination || null;
  const { apply, applying, notice, clearNotice } = useApplySuggestion({
    buildEditRequest,
    accountId,
    campaignId,
    destination,
    userId,
  });
  // A dispatched edit has to outlive this render tree. CardFeed marks the card
  // applied at once, but that state dies with the tab (each CardTab below is
  // its own mount), so refetch the runs — which now carry the edit as
  // `appliedEdits` — the way useDismissals refetches after every write.
  // Otherwise apply, switch tabs, switch back, and the Apply button was back on
  // a change already sent to the live campaign.
  const { reload: reloadRuns } = runs;
  useEffect(() => { if (refreshKey) reloadRuns(); }, [refreshKey, reloadRuns]);
  const applyAndRefresh = useCallback(async (card) => {
    const out = await apply(card);
    if (out?.ok) { reloadRuns(); onApplied?.(); }
    return out;
  }, [apply, reloadRuns, onApplied]);

  // Dismissals are account-wide: a scope of "All campaigns, this check" has to
  // suppress on every tab, not just the one it was made on.
  // The whole hook result is kept: the raw export reads its `data` and `error`.
  const dismissalsState = useDismissals(enabled ? accountId : null, userId, campaignId);
  const {
    dismissals, reasonSets, dismiss, reinstate,
  } = dismissalsState;

  // Running the three analyses on demand. Nothing schedules them any more, so
  // this is how they happen — see performanceAnalysisScheduler's header.
  const {
    refresh, refreshing, result: refreshResult, clearResult: clearRefreshResult,
    progress: refreshProgress,
  } = useRefreshAnalyses({
    accountId, userId, campaignId,
    campaignIds: embedded ? campaignIds : [],
    // For the completion count: the hook re-reads these on an interval after a
    // press and counts rows newer than the press.
    runs: runs.allRuns, reload: runs.reload,
  });

  // "Data refreshed 8 Oct, 11:52 · 3h ago" — when the analyses shown here were
  // produced, newest and (when they span more than a day) oldest. An absolute
  // date, because an export or a screenshot outlives "3h ago". Re-rendered every
  // 30s so the age keeps up without a reload.
  const lastRunAt = newestRunCompletedAt(runs.runs);
  const freshness = describeDataFreshness(runFreshness(runs.runs));
  const [, setAgeTick] = useState(0);
  useEffect(() => {
    if (lastRunAt == null) return undefined;
    const h = setInterval(() => setAgeTick((n) => n + 1), 30_000);
    return () => clearInterval(h);
  }, [lastRunAt]);
  // The force choice is PUT TO THE USER rather than decided here. The server
  // skips anything analysed in the last 20h, which is invisible from the page,
  // so a plain press shortly after a run dispatches nothing and reads as a
  // broken button. Expanding offers both, and names what each one does.
  const [refreshOpen, setRefreshOpen] = useState(false);

  const runRefresh = async (force) => {
    setRefreshOpen(false);
    if (savedReport && !campaignIds.length) return;
    await refresh({ force });
    // Re-read the tabs. The analyses themselves are asynchronous — dispatch only
    // reaches the queue — so this shows what had already completed, not the runs
    // just started. The result line below says so rather than implying otherwise.
    runs.reload?.();
  };

  // The warning shown when a reason is chosen that does NOT mean the finding was
  // wrong. Served with the reason sets so the copy stored matches the copy read.
  const dismissNotes = NOT_A_REJECTION_NOTES;

  if (settingsLoading) {
    return <div className="perf-root"><div className="perf-loading">Loading…</div></div>;
  }

  if (!enabled) {
    return (
      <div className="perf-root">
        <div className="perf-gate">
          <b>The Performance section is not enabled for this account.</b>
          {' '}It is gated on the <code>enable-performance-tab</code> account setting. An account
          admin can turn it on in account settings.
        </div>
      </div>
    );
  }

  // A `?tab=` value with no tab behind it — a bookmark of the removed Moments
  // tab, or a typo — falls back to the first tab rather than rendering nothing.
  const availableTabs = actionsOnly && !summaryContent ? TABS.filter(t => t.key !== 'account') : TABS;
  const active = availableTabs.find((t) => t.key === tab) || availableTabs[0];
  const { error } = runs;
  // The loading screen is for the FIRST load. A refetch — after an apply, so the
  // runs carry the new edit as `appliedEdits` — keeps the tabs mounted on the
  // data they already hold: unmounting them would blank the page, reset the
  // feed's filters and drop its optimistic marks for the moment it takes.
  const loading = runs.loading && !runs.data;

  /** Super-admin diagnostic: the active tab's server responses as received —
   *  /runs narrowed to this tab's analysis kind (all of them for Summary, plus
   *  the portfolio fetches), and /dismissals. See utils/performanceRawExport.js. */
  const downloadActiveTabJson = () => {
    const exported = buildPerformanceTabExport({
      tab: active,
      runs,
      dismissals: dismissalsState,
      portfolio: active.key === 'account' ? portfolioRaw : null,
      accountId,
      campaignId: campaignId || null,
    });
    downloadJson(performanceExportFilename(active, new Date(exported.exportedAt), exported.dataRefreshed), exported);
  };

  const downloadExcel = () => {
    setExportError(null);
    try {
      downloadPerformanceExcel({ runs, dismissals: dismissalsState, accountId, campaignId, campaignIds });
    } catch (err) {
      setExportError('Excel export failed. Please try again.');
    }
  };

  // A TAB BADGE IS A CALL TO ACTION, so it counts the QUEUE — what is left to
  // read — not the population. Every badge here counted the raw server arrays,
  // so after dismissing the only card the strip still read a count above a tab
  // whose own headline said 0. All three are filtered through the same
  // dismissal list.
  // Applied cards leave the queue the same way: done is not to-do.
  const live = (cards) => partitionApplied(partitionDismissed(cards, dismissals).live).live;
  const liveCards = live(runs.cards);
  // Summary must count the same diagnostic population the Diagnostics feed
  // renders. The raw run cards are only its actionable suggestion subset; the
  // run's campaign health, measurement, blocked entities, blocking issues and
  // conflicts become advisory cards in CardTab. Build those here with the same
  // entity dedupe and dismissal rules, or a feed with dozens of findings reads
  // as "2 diagnostic findings" on Summary.
  const diagnosticKeys = cardEntityKeys(
    partitionDismissed(runs.cards, dismissals).live.filter((c) => c.route === 'diagnostics' || c.alsoReportedBy?.some(source => source.agent === 'Diagnostic')),
  );
  const liveDiagnosisCards = live(diagnosisCards(
    runs.byKind?.diagnostic?.diagnoses || [],
    diagnosticKeys,
    // The account reviews in the feed: an entity one of them lists is not
    // carded again (diagnosisFindings `covered`).
    partitionDismissed(runs.cards, dismissals).live,
  ));
  // MAIN only, on every tab. Each tab puts its minor findings behind a
  // "More (N)" button that carries its own count (utils/findingTier.js); the
  // badges and the Summary strip count what leads the feed, or a badge reading
  // 134 sits above a feed that leads with 27. Same cards, same context, same
  // list rules as the tabs themselves, so the numbers agree.
  const summaryCards = mainCards([...liveCards, ...liveDiagnosisCards], runs.tierContext);
  const counts = {
    // The badge and Summary health strip describe the same Findings feed.
    diagnostics: summaryCards.filter((c) => c.route === 'diagnostics').length,
    pacing: summaryCards.filter((c) => c.route === 'pacing').length,
    recommendations: summaryCards.filter((c) => c.route === 'recommendations').length,
  };

  return (
    <div className={`perf-root${embedded ? ' perf-embedded' : ''}`}>
      {embedded && !savedReport && !campaignId && <div className="perf-shared-scope"><span>Campaigns across all tabs</span>
        <CampaignFilter multiple value={campaignIds} onChange={setCampaignIds}
          options={campaignCatalog.map(c => ({ key: c.id, label: c.name, count: 1 }))}
          allCount={campaignCatalog.length} />
        <small>Summary shows saved reporting; analysis tabs show the latest completed runs.</small>
      </div>}
      {actionsOnly && savedReport?.performanceDataset && <PortfolioTab
        accountId={accountId} userId={userId} performanceV2 overviewOnly overviewScope={overviewScope} onOverviewCampaign={onOverviewCampaign}
        campaignIds={campaignIds} savedReport={savedReport}
      />}
      <div className="perf-tabs">
        {!recommendationSections && availableTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`perf-tab${t.key === active.key ? ' on' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <span aria-hidden="true" className="material-icons">{t.icon}</span>
            {t.label}
            {counts[t.key] ? <span className="perf-tab-c">{counts[t.key]}</span> : null}
          </button>
        ))}
        {embedded && <button type="button" className="perf-raw-export" disabled={runs.loading}
          onClick={reloadRuns}>Reload results</button>}
        {runs.canRefresh ? (
          refreshOpen ? (
            <span className="perf-refresh-choice" role="group" aria-label="Refresh analyses">
              <button
                type="button"
                className="perf-refresh-opt"
                onClick={() => runRefresh(false)}
                title="Run the analyses that are missing or older than 20 hours"
              >
                Refresh stale
              </button>
              <button
                type="button"
                className="perf-refresh-opt force"
                onClick={() => runRefresh(true)}
                title="Re-run all three analyses even where one completed in the last 20 hours"
              >
                Force re-run
              </button>
              <button
                type="button"
                className="perf-refresh-opt cancel"
                onClick={() => setRefreshOpen(false)}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="perf-refresh"
              onClick={() => { clearRefreshResult(); setRefreshOpen(true); }}
              disabled={refreshing || Boolean(savedReport && !campaignIds.length)}
              title={embedded && campaignIds.length ? `Run all three analyses for the ${campaignIds.length} selected campaigns` : campaignId
                ? 'Run all three analyses for this campaign'
                : 'Run all three analyses for every campaign live in its DSP on this account'}
            >
              <span aria-hidden="true">⟳</span>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          )
        ) : null}
        {freshness ? (
          <span
            className={`perf-refreshed-ago${freshness.stale ? ' stale' : ''}`}
            title={`Most recent analysis completed ${new Date(lastRunAt).toLocaleString()}`}
          >
            {freshness.text}
          </span>
        ) : null}
        {!embedded && <button
          type="button"
          className={`perf-sidekick-toggle${sidekickOpen ? ' on' : ''}`}
          aria-expanded={sidekickOpen}
          onClick={() => setSidekickOpen((open) => !open)}
        >
          <span aria-hidden="true">✦</span>
          Sidekick agent
        </button>}
        {actingAsSuperAdmin ? (
          <button
            type="button"
            className="perf-raw-export"
            onClick={downloadExcel}
            disabled={runs.loading || !!runs.error || !runs.data || dismissalsState.loading || !!dismissalsState.error}
            title="Download all diagnostics, pacing and optimizations in one Excel workbook"
          >
            <span aria-hidden="true">⤓</span>
            Download Excel
          </button>
        ) : null}
        {actingAsSuperAdmin ? (
          <button
            type="button"
            className="perf-raw-export"
            onClick={downloadActiveTabJson}
            disabled={loading}
            title={`Download the ${active.label} tab's server responses as raw JSON (super admin only)`}
          >
            <span aria-hidden="true">⤓</span>
            {active.label} JSON
          </button>
        ) : null}
      </div>

      {exportError && <div className="perf-error" role="alert">{exportError}</div>}

      {sidekickOpen ? (
        <PerformanceSidekick
          open
          setOpen={setSidekickOpen}
          accountId={accountId}
          userId={userId}
          campaignId={campaignId || null}
          campaign={runs.campaign}
          campaigns={runs.campaigns}
          accountSettings={accountSettings}
        />
      ) : null}

      {/* The outcome of the page's primary action, announced. It was inserted
          silently into a plain div far from the button that was pressed, so a
          screen-reader user got no confirmation that an edit run had started —
          or that it had failed. The wrapper is always mounted so the region
          exists before the text lands in it. */}
      <div role="status" aria-live="polite">
        {(refreshProgress || refreshResult) ? (
          <div className={`perf-notice${(refreshProgress || refreshResult.ok) ? ' ok' : ' bad'}`}>
            {/* While analyses are landing, the progress line replaces the
                "Started N…" summary — and when they have all landed it says so,
                which is the sentence a client waiting on the page is looking for. */}
            <span>{refreshProgress ? describeRefreshProgress(refreshProgress) : describeRefresh(refreshResult)}</span>
            <button type="button" className="btn btn-gho btn-sm" onClick={clearRefreshResult}>
              Dismiss
            </button>
          </div>
        ) : null}
        {notice ? (
          <div className={`perf-notice${notice.ok ? ' ok' : ' bad'}`}>
            <span>{notice.text}</span>
            <button type="button" className="btn btn-gho btn-sm" onClick={clearNotice}>
              Dismiss
            </button>
          </div>
        ) : null}
      </div>

      {loading ? <div className="perf-loading">Loading the {active.label} run…</div> : null}

      {!loading && error === 'not-enabled' ? (
        <div className="perf-gate">
          <b>The server refused this request.</b> The Performance section is not enabled for this
          account.
        </div>
      ) : null}

      {!loading && error && error !== 'not-enabled' ? (
        <div className="perf-error">{error}</div>
      ) : null}

      {!loading && !error ? (
        <>
          {active.key === 'account' ? ((typeof summaryContent === 'function' ? summaryContent(setTab) : summaryContent) ||
            <PortfolioTab
              key={`${accountId}-${campaignId}-${performanceV2}`}
              performanceV2={performanceV2}
              accountId={accountId}
              userId={userId}
              campaign={runs.campaign}
              // The SAME dismissal filter the badges use. The health strip
              // counts open findings, so a finding someone dismissed must not
              // keep the account looking unhealthy — and the count here must
              // agree with the count on the tab beside it.
              cards={summaryCards}
              // Per-campaign run findings, for the table's Competitive and
              // Quality groups. NOT filtered through `live`: a dismissal is a
              // decision about a CARD in the feed, and the diagnostic's own
              // health counts and the growth run's constraint verdict are not
              // cards — dismissing "Add assets to 10 asset groups" does not make
              // the ten asset groups start delivering.
              runsByCampaign={runs.byCampaign}
              onRawData={setPortfolioRaw}
              savedReport={savedReport}
              campaignIds={embedded ? campaignIds : undefined}
              onViewRecommendations={embedded ? () => setTab('recommendations') : undefined}
            />
          ) : null}

          {(recommendationSections ? recommendationSections.includes('diagnostics') : active.key === 'diagnostics') ? (
            <CardTab
              route="diagnostics"
              title="Diagnostics"
              analysisKind="diagnostic"
              showCampaignFilter={!embedded}
              analyzedCampaignCount={new Set(runs.runs.filter(run => run.analysisKind === 'diagnostic').map(run => run.campaignId)).size}
              unit={`finding${counts.diagnostics === 1 ? '' : 's'} from the diagnostic run`}
              campaign={runs.campaign}
              campaigns={runs.campaigns}
              cards={runs.cards}
              tierContext={runs.tierContext}
              missing={runs.missing}
              methodology={METHODOLOGY.diagnostics}
              diagnoses={runs.byKind?.diagnostic?.diagnoses || []}
              suppressed={runs.byKind?.diagnostic?.suppressed || []}
              onApply={applyAndRefresh}
              applying={applying}
              onDismiss={dismiss}
              onReinstate={reinstate}
              dismissals={dismissals}
              reasonSets={reasonSets}
              notes={dismissNotes}
            />
          ) : null}

          {(recommendationSections ? recommendationSections.includes('pacing') : active.key === 'pacing') ? (
            <CardTab
              route="pacing"
              title="Budget & pacing"
              analysisKind="pacing"
              showCampaignFilter={!embedded}
              analyzedCampaignCount={new Set(runs.runs.filter(run => run.analysisKind === 'pacing').map(run => run.campaignId)).size}
              unit={`pacing recommendation${counts.pacing === 1 ? '' : 's'}`}
              campaign={runs.campaign}
              campaigns={runs.campaigns}
              cards={runs.cards}
              tierContext={runs.tierContext}
              missing={runs.missing}
              methodology={METHODOLOGY.pacing}
              pacingDelta={campaignId ? runs.pacingDeltas?.[campaignId] || null : null}
              suppressed={runs.byKind?.pacing?.suppressed || []}
              onApply={applyAndRefresh}
              applying={applying}
              onDismiss={dismiss}
              onReinstate={reinstate}
              dismissals={dismissals}
              reasonSets={reasonSets}
              notes={dismissNotes}
            />
          ) : null}

          {(recommendationSections ? recommendationSections.includes('recommendations') : active.key === 'recommendations') ? (
            <CardTab
              route="recommendations"
              key={`${accountId}-${campaignId}-${performanceV2}`}
              performanceV2={performanceV2}
              title={recommendationSections ? "Performance recommendations" : "Optimization"}
              analysisKind="performance"
              showCampaignFilter={!embedded}
              analyzedCampaignCount={new Set(runs.runs.filter(run => run.analysisKind === 'performance').map(run => run.campaignId)).size}
              unit={`recommendation${counts.recommendations === 1 ? '' : 's'} from the performance run`}
              campaign={runs.campaign}
              campaigns={runs.campaigns}
              cards={runs.cards}
              tierContext={runs.tierContext}
              missing={runs.missing}
              methodology={METHODOLOGY.recommendations}
              suppressed={runs.byKind?.performance?.suppressed || []}
              googleReview={runs.byKind?.performance?.googleReview || []}
              onApply={applyAndRefresh}
              applying={applying}
              onDismiss={dismiss}
              onReinstate={reinstate}
              dismissals={dismissals}
              reasonSets={reasonSets}
              notes={dismissNotes}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
